/**
 * Sweeping stale `kc-callback-*` entries out of localStorage.
 *
 * keycloak-js parks one entry per login attempt -- the OIDC state, nonce,
 * redirect URI and PKCE code_verifier -- and removes it when it parses the
 * matching callback. An attempt that never comes back (the reader closed the
 * tab on the Keycloak screen, hit an error, or started a second login) leaves
 * its entry behind, and keycloak-js only prunes expired ones when it happens to
 * be parsing a callback. So they accumulate.
 *
 * These are not tokens. They are single-use login scratch state, and a stale
 * one can no longer complete a login -- but leaving a PKCE code_verifier in
 * localStorage indefinitely is still worth not doing, and the pile is confusing
 * to anyone auditing storage.
 *
 * The rule mirrors keycloak-js's own: an entry is stale once its recorded
 * `expires` has passed. Nothing unexpired is ever touched, because a second tab
 * may be mid-login with it -- deleting that entry would break a login that was
 * about to succeed.
 */

/** keycloak-js's prefix for per-attempt callback state. */
export const KC_CALLBACK_PREFIX = "kc-callback-";

/**
 * Which of `keys` are keycloak callback entries that can no longer be used.
 *
 * A value that will not parse, or carries no usable `expires`, counts as stale:
 * keycloak-js cannot complete a login from it either, so it is dead weight.
 * A value whose `expires` is still in the future is left alone.
 */
export function staleCallbackKeys(
  keys: readonly string[],
  getItem: (key: string) => string | null,
  now: number,
): string[] {
  const stale: string[] = [];
  for (const key of keys) {
    if (!key.startsWith(KC_CALLBACK_PREFIX)) continue;
    const raw = getItem(key);
    if (raw === null) continue;
    let expires: unknown;
    try {
      expires = (JSON.parse(raw) as { expires?: unknown })?.expires;
    } catch {
      stale.push(key);
      continue;
    }
    if (typeof expires !== "number" || !Number.isFinite(expires)) {
      stale.push(key);
      continue;
    }
    if (expires <= now) stale.push(key);
  }
  return stale;
}

/**
 * Remove them. Every storage call is guarded: localStorage throws in a
 * sandboxed iframe and when cookies are blocked, and a failed cleanup must
 * never take the sign-in down with it.
 */
export function sweepStaleCallbackState(now: number = Date.now()): number {
  let store: Storage | null = null;
  try {
    store = (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return 0;
  }
  if (!store) return 0;

  // Enumerated through the Storage API's own length/key(i), not Object.keys:
  // the latter happens to work on a real localStorage because its entries are
  // own enumerable properties, but that is incidental and it also picks up
  // anything else hanging off the object.
  //
  // Collected in full BEFORE removing anything, because removeItem reindexes
  // and iterating while deleting would skip entries.
  const keys: string[] = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k !== null) keys.push(k);
    }
  } catch {
    return 0;
  }

  const stale = staleCallbackKeys(keys, (k) => {
    try { return store.getItem(k); } catch { return null; }
  }, now);

  let removed = 0;
  for (const key of stale) {
    try { store.removeItem(key); removed++; } catch { /* leave it rather than fail */ }
  }
  return removed;
}
